import { useQuery } from "react-query";
import { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import {
  getMatchRateBreakdown,
  getMatchRateSummary,
} from "../api/matchRate.api";
import {
  MatchRateFilters,
  MatchRateGroupBy,
} from "../types/matchRate.types";

export function useDebouncedValue<T>(value: T, delayMs: number) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const timeout = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timeout);
  }, [value, delayMs]);

  return debounced;
}

export function useMatchRateData(
  filters: MatchRateFilters,
  groupBy: MatchRateGroupBy
) {
  const filterKey = JSON.stringify(filters);
  const debouncedFilterKey = useDebouncedValue(filterKey, 300);
  const debouncedFilters = useMemo(
    () => JSON.parse(debouncedFilterKey) as MatchRateFilters,
    [debouncedFilterKey]
  );

  const summaryQuery = useQuery(
    ["matchRateSummary", debouncedFilterKey],
    () => getMatchRateSummary(debouncedFilters),
    { retry: false, keepPreviousData: true }
  );

  const breakdownQuery = useQuery(
    ["matchRateBreakdown", debouncedFilterKey, groupBy],
    () =>
      getMatchRateBreakdown({
        ...debouncedFilters,
        groupBy,
      }),
    { retry: false, keepPreviousData: true }
  );

  const error = summaryQuery.error ?? breakdownQuery.error;
  const status = (error as AxiosError | undefined)?.response?.status;

  return {
    summary: summaryQuery.data,
    breakdown: breakdownQuery.data,
    isInitialLoading:
      (summaryQuery.isLoading && !summaryQuery.data) ||
      (breakdownQuery.isLoading && !breakdownQuery.data),
    error,
    isForbidden: status === 403,
  };
}
